import { GeneralConfig } from '../config/generalConfig';
import { getCompensationPlan } from '../stores/moduleStore';
import { Country } from '../types/Country';
import { CompensationPlan } from '../types/enums/CompensationPlan';
import { UserRole } from '../types/enums/UserRole';
import { Order } from '../types/Order';
import { RecentOrdersPaginated } from '../types/RecentOrdersPaginated';
import { User } from '../types/User';
import { UserKyc } from '../types/UserKyc';
import { UserSearch } from '../types/UserSearch';
import { UnauthorizedException } from '../utils/apiException';
import { ApiService } from './apiService';

export class UserService {
  private apiService = new ApiService();

  async getAuthUserData(): Promise<User> {
    const response = await this.apiService.get('/api/user_data');
    const json = response.data as Record<string, any>;
    return User.fromJson(json);
  }

  async getUserRole(): Promise<UserRole | null> {
    const response = await this.apiService.get('/api/user_role');
    const json = response.data as UserRole;
    return json;
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await this.apiService.get('/api/auth_check');
      return true;
    } catch (e) {
      if (e instanceof UnauthorizedException) {
        return false;
      }
      throw e;
    }
  }

  async getSignupData(referral?: string): Promise<Record<string, any>> {
    const response = await this.apiService.get('/api/users/signup_data', {
      queryParams: { referral },
      authenticate: true,
    });
    const json = response.data as Record<string, any>;

    return {
      defaultSponsor: json['default_sponsor'] as Record<string, any>,
      passwordPolicyEnabled: json['password_policy'] as boolean,
      otpVerification: json['otp_verification'] as boolean,
      otpBasedOn: json['otp_based_on'] as string,
      defaultLeg: json['default_leg'] as string | null,
      referralId: json['referral_id'] as number | null,
      isPhoneUnique: json['phone_unique'] as boolean,
      isEmailUnique: json['email_unique'] as boolean,
      companyPrivacyUrl: json['company_privacy_url'] as string,
      companyTermsUrl: json['company_terms_url'] as string,
      countries: (json['countries'] as Array<Record<string, any>>).map((e) =>
        Country.fromJson(e)
      ),
      showSignup: json['show_signup_page'] as boolean | undefined,
    };
  }

  async signupValidateReferral(
    referral?: string
  ): Promise<Record<string, any>> {
    const response = await this.apiService.get(
      '/api/users/signup_validate_referral',
      {
        queryParams: { referral },
        authenticate: true,
      }
    );
    const json = response.data as Record<string, any>;

    return {
      defaultLeg: json['default_leg'] as string | null,
      referralId: json['referral_id'] as number | null,
    };
  }

  async signup(params: {
    referral: string;
    firstname: string;
    lastname?: string;
    email?: string;
    phoneCode?: string;
    phone?: string;
    username?: string;
    password: string;
    placement?: string;
    position?: string;
  }): Promise<number | null> {
    const data: Record<string, string> = {};

    const compensationPlan = getCompensationPlan();

    if (
      compensationPlan &&
      [CompensationPlan.Binary, CompensationPlan.Matrix].includes(
        compensationPlan
      )
    ) {
      if (params.placement) data['placement'] = params.placement;
    }
    if (compensationPlan === CompensationPlan.Binary) {
      if (params.position) data['position'] = params.position;
    }

    data['referral_key'] = 'username';
    data['referral'] = params.referral;
    data['first_name'] = params.firstname;
    if (params.lastname) data['last_name'] = params.lastname;
    if (params.email) data['email'] = params.email;
    if (params.phoneCode) data['mobile_code'] = params.phoneCode;
    if (params.phone) data['mobile'] = params.phone;
    if (params.username) data['username'] = params.username;
    data['password'] = params.password;

    const response = await this.apiService.post('/api/users/signup', data, {
      authenticate: true,
    });
    const json = response.data as number | null;
    return json;
  }

  async resendOtp(id: number): Promise<void> {
    const data = { key: id.toString() };
    await this.apiService.post('/api/users/resend_signup_verification', data);
  }

  async verifySignup(id: number, otp: string, otp2?: string): Promise<void> {
    const data: Record<string, string> = {
      key: id.toString(),
      otp,
    };
    if (otp2) data['otp2'] = otp2;

    await this.apiService.post('/api/users/verify_signup', data);
  }

  async getAdminData(): Promise<Record<string, any>> {
    const response = await this.apiService.get('/api/users/admin_info', {
      authenticate: true,
    });
    const json = response.data as Record<string, any>;
    return json;
  }

  async getRecentOrdersPaginated(
    cursor?: string
  ): Promise<RecentOrdersPaginated> {
    const params: Record<string, string> = {};
    if (cursor) params['cursor'] = cursor;

    const response = await this.apiService.get('/api/orders/recent_orders', {
      queryParams: params,
    });
    const json = response.data as Record<string, any>;
    return RecentOrdersPaginated.fromJson(json);
  }

  async getOrderDetails(orderId: number): Promise<Order> {
    const response = await this.apiService.get(`/api/orders/${orderId}`);
    const json = response.data as Record<string, any>;
    return Order.fromJson(json);
  }

  async searchSponsorDownlines(
    keyword: string,
    levels?: number
  ): Promise<UserSearch[]> {
    const params: Record<string, string> = { keyword };
    if (levels) params['levels'] = levels.toString();

    const response = await this.apiService.get('/api/users/sponsor_downlines', {
      queryParams: params,
    });
    const json = response.data as Array<any>;
    return json.map((e) => UserSearch.fromJson(e as Record<string, any>));
  }

  async searchPlacementDownlines(
    keyword: string,
    levels?: number
  ): Promise<UserSearch[]> {
    const params: Record<string, string> = { keyword };
    if (levels) params['levels'] = levels.toString();

    const response = await this.apiService.get(
      '/api/users/placement_downlines',
      { queryParams: params }
    );
    const json = response.data as Array<any>;
    return json.map((e) => UserSearch.fromJson(e as Record<string, any>));
  }

  async deleteUser(password: string): Promise<void> {
    await this.apiService.delete('/api/users', {
      data: { password },
      includeStoreToken: GeneralConfig.hasStoreModule,
    });
  }

  async getAllKycDocuments(): Promise<UserKyc[]> {
    const response = await this.apiService.get('/api/kyc/kyc_details');
    const json = response.data as Array<any>;
    return json.map((e) => UserKyc.fromJson(e as Record<string, any>));
  }

  async getAllKycCategories(): Promise<Record<string, any>[]> {
    const response = await this.apiService.get('/api/kyc/kyc_category');
    const json = response.data as Array<any>;
    return json.map((e) => e as Record<string, any>);
  }

  async submitKyc(
    data: Record<string, any>,
    filePaths: string[],
    files?: File[]
  ): Promise<void> {
    await this.apiService.postMultipart(
      '/api/kyc/submit',
      'documents[]',
      // filePaths,
      [],
      {
        data,
      }
    );
  }

  async removeKyc(id: number): Promise<void> {
    await this.apiService.delete(`/referrals/delete_kyc/${id}`);
  }
}
